import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MediaCard from "@/components/feature/MediaCard";
import { categoryIntro, genreMap, type MediaType } from "@/mocks/media";
import { getMediaByType, parseMediaType } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 12;

type SortKey = "latest" | "rating" | "hot";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新上映" },
  { key: "rating", label: "评分最高" },
  { key: "hot", label: "热门推荐" },
];

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const mediaType = parseMediaType(type) as MediaType | null;

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setSelectedGenres([]);
    setSort("latest");
    setPage(1);
  }, [type]);

  const result = useMemo(() => {
    if (!mediaType) return [];
    let list = getMediaByType(mediaType);
    if (selectedGenres.length > 0) {
      list = list.filter((item) =>
        item.genres.some((g) => selectedGenres.includes(g)),
      );
    }
    const sorted = [...list];
    if (sort === "latest") {
      sorted.sort((a, b) => Number(b.year) - Number(a.year) || b.heat - a.heat);
    } else if (sort === "rating") {
      sorted.sort((a, b) => b.rating - a.rating);
    } else {
      sorted.sort((a, b) => b.heat - a.heat);
    }
    return sorted;
  }, [mediaType, selectedGenres, sort]);

  const intro = mediaType ? categoryIntro[mediaType] : null;

  usePageMeta({
    title: intro
      ? `${intro.title} - po18娱乐内容聚合平台`
      : "分类 - po18娱乐内容聚合平台",
    description: intro
      ? `${intro.desc} 最新${intro.title}推荐与影视资讯，尽在 po18 娱乐内容聚合平台。`
      : "po18 娱乐内容聚合平台分类页。",
    keywords: intro
      ? `po18,${intro.title},${intro.title}推荐,娱乐内容聚合`
      : "po18,娱乐内容聚合",
  });

  if (!mediaType || !intro) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-24 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl">
            <i className="ri-error-warning-line" />
          </span>
          <h1 className="mt-5 text-xl font-bold text-foreground-900">
            未找到该分类
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            请从下方选择你想浏览的影视类型。
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/category/movie"
              className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              电影
            </Link>
            <Link
              to="/category/tv"
              className="px-5 py-2.5 rounded-md bg-secondary-100 text-secondary-900 text-sm font-semibold hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              电视剧
            </Link>
            <Link
              to="/category/variety"
              className="px-5 py-2.5 rounded-md bg-secondary-100 text-secondary-900 text-sm font-semibold hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              综艺
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = result.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  const genres = genreMap[mediaType];

  const toggleGenre = (genre: string) => {
    setPage(1);
    setSelectedGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre],
    );
  };

  const resetAll = () => {
    setSelectedGenres([]);
    setSort("latest");
    setPage(1);
  };

  const hasFilter = selectedGenres.length > 0 || sort !== "latest";

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6">
        {/* 面包屑 */}
        <nav className="pt-6 flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800 font-medium">{intro.title}</span>
        </nav>

        {/* 标题与说明 */}
        <header className="mt-4 rounded-lg bg-gradient-to-r from-primary-50 to-background-100 border border-background-200/70 p-6 md:p-8">
          <h1 className="flex items-center gap-3 text-2xl md:text-3xl font-black text-foreground-950">
            <span className="w-10 h-10 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
              <i
                className={`text-xl ${
                  mediaType === "movie"
                    ? "ri-film-line"
                    : mediaType === "tv"
                      ? "ri-tv-2-line"
                      : "ri-mic-line"
                }`}
              />
            </span>
            {intro.title}
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            {intro.desc}
          </p>
        </header>

        {/* 主体：左筛选 + 右列表 */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
          {/* 筛选栏 */}
          <aside className="lg:sticky lg:top-20 h-fit rounded-lg bg-background-100 border border-background-200/70 p-5">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-900">
                <i className="ri-filter-3-line text-base text-primary-600" />
                类型筛选
              </h2>
              {hasFilter && (
                <button
                  type="button"
                  onClick={resetAll}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                >
                  重置
                </button>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {genres.map((genre) => {
                const active = selectedGenres.includes(genre);
                return (
                  <button
                    key={genre}
                    type="button"
                    onClick={() => toggleGenre(genre)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      active
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-200/70 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
                    }`}
                  >
                    {genre}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 pt-5 border-t border-background-200/70">
              <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-900 mb-3">
                <i className="ri-sort-desc text-base text-primary-600" />
                排序方式
              </h2>
              <div className="flex flex-col gap-2">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      setSort(opt.key);
                      setPage(1);
                    }}
                    className={`text-left px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                      sort === opt.key
                        ? "bg-primary-100 text-primary-700"
                        : "text-foreground-600 hover:bg-background-200/60"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* 内容列表 */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-foreground-500">
                共 <span className="text-primary-600 font-semibold">{result.length}</span> 个结果
              </p>
              <p className="text-sm text-foreground-500 hidden sm:block">
                第 {currentPage} / {totalPages} 页
              </p>
            </div>

            {result.length === 0 ? (
              <div className="rounded-lg bg-background-100 border border-background-200/70 py-20 text-center">
                <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-400 flex items-center justify-center text-3xl">
                  <i className="ri-inbox-line" />
                </span>
                <h3 className="mt-5 text-lg font-bold text-foreground-900">
                  没有找到符合条件的内容
                </h3>
                <p className="mt-2 text-sm text-foreground-500">
                  试试更换筛选条件，或重置查看全部{intro.title}。
                </p>
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-lg" />
                  重置筛选
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {paged.map((item) => (
                    <MediaCard key={item.id} item={item} showType={false} />
                  ))}
                </div>

                {/* 分页 */}
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setPage(currentPage - 1)}
                      className="w-9 h-9 rounded-md bg-background-100 border border-background-200 text-foreground-700 flex items-center justify-center hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      aria-label="上一页"
                    >
                      <i className="ri-arrow-left-s-line text-lg" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPage(p)}
                        className={`w-9 h-9 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                          p === currentPage
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-100 border border-background-200 text-foreground-700 hover:bg-background-200"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage(currentPage + 1)}
                      className="w-9 h-9 rounded-md bg-background-100 border border-background-200 text-foreground-700 flex items-center justify-center hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      aria-label="下一页"
                    >
                      <i className="ri-arrow-right-s-line text-lg" />
                    </button>
                  </div>

                  {currentPage < totalPages && (
                    <button
                      type="button"
                      onClick={() => setPage(currentPage + 1)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-secondary-100 text-secondary-900 text-sm font-semibold hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line text-lg" />
                      加载更多
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}